import { Fragment, ReactNode, useCallback, useMemo } from "react"
import {
  Block,
  Button,
  ButtonProps,
  ChevronDown,
  StatefulPopover,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  DepositWithWithdrawalStatus,
  RootState,
  selectTokenDecimals,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import { useDepositBox } from "#/shared/hooks"
import { useMinBridgeAmount } from "#/shared/hooks/bridge"
import { BridgeableTokens } from "#/shared/types"
import { convertDecimalsToTokenDecimals } from "#/shared/utils"

import OverviewBridgeDepositButton from "./OverviewBridgeDepositButton"

type OverviewUnbridgedAssetsItemActionsProps = {
  deposit: DepositWithWithdrawalStatus
  isDepositLocked: boolean
  isDepositBridgeable: boolean
}

function OverviewUnbridgedAssetsItemActionDropdown({
  elements,
}: {
  elements: ReactNode[]
}) {
  const [css, theme] = useStyletron()

  if (!elements.length) return null

  if (elements.length === 1) return elements[0]

  return (
    <StatefulPopover
      autoFocus={false}
      placement="bottom"
      content={
        <Block className={css({ display: "flex", flexDirection: "column" })}>
          {elements.map((el, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={index}>{el}</Fragment>
          ))}
        </Block>
      }
      overrides={{
        Body: { style: () => ({ minWidth: "100px" }) },
        Inner: {
          style: () => ({ background: theme.colors.buttonSecondaryFill }),
        },
      }}
    >
      <Button
        $as="div"
        shape="pill"
        kind="secondary"
        size="small"
        endEnhancer={() => <ChevronDown size={theme.sizing.scale600} />}
        overrides={{ BaseButton: { style: { width: "100px" } } }}
      >
        Actions
      </Button>
    </StatefulPopover>
  )
}

export default function OverviewUnbridgedAssetsItemActions({
  deposit,
  isDepositLocked,
  isDepositBridgeable,
}: OverviewUnbridgedAssetsItemActionsProps) {
  const { token, mintedAmount, depositId, amount } = deposit

  const { openWithdrawalModal, openReturnStbtcModal } = useDepositBox(deposit)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const { tBTC } = useDappSelector(selectWalletBalances)

  const { data: minTokenAmount, isFetching: isWaitingForMinBridgeAmount } =
    // The `BTC` deposit is represented as `tBTC` in the Portal contract so the
    // `MezoBridge.minTBTCAmount` requirement must be met.
    useMinBridgeAmount(token === "BTC" ? "tBTC" : (token as BridgeableTokens))

  const isDepositAmountEnough = useMemo(() => {
    if (isWaitingForMinBridgeAmount) return false

    // For BTC deposits we need to pretend as it is tBTC
    const decimals = Number(token === "BTC" ? tBTC.decimals : tokenDecimals)

    return (
      convertDecimalsToTokenDecimals(BigInt(amount), decimals) >= minTokenAmount
    )
  }, [
    tBTC,
    amount,
    minTokenAmount,
    isWaitingForMinBridgeAmount,
    tokenDecimals,
    token,
  ])

  const hasStbtcToReturn = useMemo(
    () => BigInt(mintedAmount) > 0n,
    [mintedAmount],
  )

  const canBeBridged = useMemo(
    () => isDepositAmountEnough && isDepositBridgeable,
    [isDepositAmountEnough, isDepositBridgeable],
  )

  const getWithdrawButton = useCallback(
    ({ shape }: Pick<ButtonProps, "shape">) => (
      <Button
        shape={shape}
        kind="secondary"
        size="small"
        onClick={openWithdrawalModal}
      >
        Withdraw
      </Button>
    ),
    [openWithdrawalModal],
  )

  if (hasStbtcToReturn)
    return (
      <Button
        shape="pill"
        kind="secondary"
        size="small"
        onClick={openReturnStbtcModal}
      >
        Return stBTC
      </Button>
    )

  if (isDepositLocked) {
    return canBeBridged ? (
      <OverviewBridgeDepositButton shape="pill" depositId={depositId} />
    ) : (
      <Button disabled kind="secondary" shape="pill" size="small">
        Locked
      </Button>
    )
  }

  return (
    <OverviewUnbridgedAssetsItemActionDropdown
      elements={[
        ...(canBeBridged
          ? [
              <OverviewBridgeDepositButton
                shape={isDepositLocked ? "pill" : "default"}
                depositId={depositId}
              />,
            ]
          : []),
        ...(isDepositLocked
          ? []
          : [
              getWithdrawButton({
                shape: isDepositAmountEnough ? "default" : "pill",
              }),
            ]),
      ]}
    />
  )
}
